import * as React from 'react'
import { forwardRef, useEffect, useRef } from 'react'

interface Props {
    text: string
    isVisible: boolean
    width?: number
    height?: number
    textConfig?: {
        fontSize: number
        padding: number
        lineHeight: number
        textBoxHeight: number
        lines: string[]
        hasText?: boolean
        yOffset?: number
        linePositions?: number[]
    }
    textAlignment?: 'left' | 'center'
}

/**
 * Canvas component for rendering disclaimer text preview
 * Purely presentational component that relies on calculations from useDisclaimerText
 */
export const DisclaimerTextCanvasPreview = forwardRef<HTMLCanvasElement, Props>(
    ({ isVisible, width = 1024, height = 1024, text, textConfig, textAlignment = 'left' }, ref) => {
        const canvasRef = useRef<HTMLCanvasElement>(null)
        const renderIdRef = useRef<string | null>(null)
        const previousTextRef = useRef<string>(text)

        // Forward the ref
        React.useImperativeHandle(ref, () => canvasRef.current!)

        // Default config - only used if textConfig is not provided
        // This should rarely happen as the component should always receive textConfig from useDisclaimerText
        const defaultConfig = {
            fontSize: 20,
            padding: 20,
            lineHeight: 23,
            textBoxHeight: height * 0.1,
            lines: text.split('\n'),
            hasText: text.trim() !== '',
            yOffset: height - (height * 0.1),
            linePositions: text.split('\n').map((_, index) => 20 + (index * 23))
        }

        // Use provided textConfig or fall back to default
        const config = { ...defaultConfig, ...textConfig }

        // Clear canvas function
        const clearCanvas = React.useCallback(() => {
            const canvas = canvasRef.current
            if (!canvas) return

            const ctx = canvas.getContext('2d')
            if (!ctx) return

            // Ensure we clear the entire canvas
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            renderIdRef.current = null
        }, [])

        // Force clear when text is emptied
        useEffect(() => {
            if (previousTextRef.current && !text) {
                clearCanvas()
            }
            previousTextRef.current = text
        }, [text, clearCanvas])

        // Render text to canvas
        useEffect(() => {
            const canvas = canvasRef.current
            if (!canvas) return

            const ctx = canvas.getContext('2d')
            if (!ctx) return

            // Always clear the canvas first to prevent ghosting
            clearCanvas()

            // If not visible or no text, just keep the canvas clear
            if (!isVisible || !text.trim() || !config.hasText) {
                renderIdRef.current = null
                return
            }

            // Generate a unique render ID based on the current props
            const currentRenderID = `${text}-${width}-${height}-${textAlignment}-${config.fontSize}-${config.yOffset}`

            // Skip rendering if this exact configuration was just rendered
            if (renderIdRef.current === currentRenderID) {
                return
            }

            // Update the render ID
            renderIdRef.current = currentRenderID

            // Draw background
            ctx.fillStyle = 'rgba(0, 0, 0, 0.4)'
            ctx.fillRect(0, config.yOffset, width, config.textBoxHeight)

            // Draw text
            ctx.fillStyle = '#D3D3D3'
            ctx.font = `${config.fontSize}px Arial`
            ctx.textBaseline = 'top'

            config.lines.forEach((line, index) => {
                // Apply horizontal margin based on alignment
                const x = textAlignment === 'center' ? width / 2 : config.padding

                // Use the line positions calculated by useDisclaimerText
                const y = config.yOffset + config.linePositions[index]

                if (textAlignment === 'center') {
                    ctx.textAlign = 'center'
                } else {
                    ctx.textAlign = 'left'
                }

                ctx.fillText(line, x, y)
            })
        }, [isVisible, width, height, text, textConfig, textAlignment, config, clearCanvas])

        // Clear canvas when component unmounts or when isVisible changes to false
        useEffect(() => {
            if (!isVisible) {
                clearCanvas()
            }

            return () => {
                clearCanvas()
            }
        }, [isVisible, clearCanvas])

        // Clear canvas when dimensions change
        useEffect(() => {
            clearCanvas()
        }, [width, height, clearCanvas])

        return (
            <canvas
                ref={canvasRef}
                width={width}
                height={height}
                style={{
                    position: 'absolute',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    pointerEvents: 'none',
                    opacity: isVisible && config.hasText && text.trim() ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                    zIndex: 3,
                    display: isVisible && config.hasText && text.trim() ? 'block' : 'none'
                }}
            />
        )
    }
)

DisclaimerTextCanvasPreview.displayName = 'DisclaimerTextCanvasPreview'
